import React, { useEffect, useState, useRef } from "react";
import { DataGrid, gridClasses, GridMoreVertIcon } from "@mui/x-data-grid";
import {
  IconButton,
  MenuItem,
  Popover,
  Modal,
  Select,
  FormControl,
  Autocomplete,
  TextField,
  Button,
} from "@mui/material";
import MatricIdsListModal from "../../components/Model/MatricIdsListModal";
import SelectMatricModal from "../../components/Model/SelectMatricModal";
import { CiEdit } from "react-icons/ci";
import { RxPlus, RxCross1 } from "react-icons/rx";
import { TbAlertHexagonFilled } from "react-icons/tb";
import { MdClose } from "react-icons/md";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { HttpService } from "../../service/HttpService";
import { toast } from "react-toastify";
import FullScreenLoader from "../../components/Loader/FullScreenLoader";
import { GoTrash } from "react-icons/go";

const resolveTagValue = (inputVal) => {
  if (!inputVal) return "";
  const valStr = String(inputVal).trim();
  const prefix = "1A405030001A4510000";
  if (valStr === "" || valStr === prefix) return "";
  if (valStr.startsWith(prefix)) {
    const after = valStr.substring(prefix.length).replace(/\D/g, "");
    if (!after) return "";
    const padded = after.padStart(5, "0");
    return prefix + padded;
  } else {
    if (valStr.length === 24) return valStr;
    const cleanVal = valStr.replace(/\D/g, "");
    if (!cleanVal) return "";
    const padded = cleanVal.padStart(5, "0");
    return prefix + padded;
  }
};

const ReadyToPackage = () => {
  const httpService = new HttpService();

  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [cultivars, setCultivars] = useState([]);
  const [importTags, setImportTags] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRowId, setMenuRowId] = useState(null);

  // Inline editing state
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});

  const [selectedActionId, setSelectedActionId] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  // Add Child Package Modal state
  const [addChildModalOpen, setAddChildModalOpen] = useState(false);
  const [addChildRow, setAddChildRow] = useState(null);
  const [deletedPackageIds, setDeletedPackageIds] = useState([]);
  const [addChildEntries, setAddChildEntries] = useState([
    {
      bulk_smalls_trim: "",
      child_package_tag: "1A405030001A4510000",
      weight: "",
      location: "",
    },
  ]);

  // Matric modal states
  const [matricModalOpen, setMatricModalOpen] = useState(false);
  const [selectMatricModalOpen, setSelectMatricModalOpen] = useState(false);
  const [selectedMatricRow, setSelectedMatricRow] = useState(null);
  const [searchData, setSearchData] = useState("");

  const handleOpenMatricModal = (row) => {
    setSelectedMatricRow(row);
    setMatricModalOpen(true);
  };

  const editingIdRef = useRef(editingId);
  const editValuesRef = useRef(editValues);

  useEffect(() => {
    editingIdRef.current = editingId;
    editValuesRef.current = editValues;
  }, [editingId, editValues]);

  const fetchReadyToPackageData = async () => {
    setLoading(true);
    try {
      const res = await httpService.get(
        `harvest-readytopackage?source_package_tag=${searchData}`,
      );
      setPackages(res?.data || []);
    } catch (e) {
      console.error(e);
      setPackages([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchReadyToPackageData();
  }, [searchData]);

  useEffect(() => {
    const fetchImportTags = async () => {
      try {
        const res = await httpService.get("harvestimport?assigned=false");
        const list = res?.data?.data || res?.data || [];
        setImportTags(list);
      } catch (e) {
        console.error("Error fetching import tags:", e);
      }
    };
    fetchImportTags();
  }, []);

  // Handle click outside to auto-save
  useEffect(() => {
    const handleClickOutside = (event) => {
      const currentId = editingIdRef.current;
      const currentValues = editValuesRef.current;

      // Ignore cancel button clicks
      const isCancelClick = !!event.target.closest(
        ".MuiIconButton-colorError, .btn-cancel, .cancel-btn, .cancel-btn-wrap, .btn-border",
      );
      if (isCancelClick) return;

      const isModalOrPopover = !!event.target.closest(
        ".MuiPopover-root, .MuiDialog-root, .MuiModal-root, .MuiBackdrop-root, .MuiPickersPopper-root, .MuiPickersLayout-root, .MuiAutocomplete-popper, .MuiPopper-root, .modal_block, .modal_header, .modal_content, .modal_footer",
      );
      if (isModalOrPopover) return;
      if (!document.body.contains(event.target)) return;

      if (currentId && currentValues && Object.keys(currentValues).length > 0) {
        const isInsideRow = event.target.closest(
          `.MuiDataGrid-row[data-id="${currentId}"], input, textarea, select, .MuiSelect-select`,
        );
        if (!isInsideRow) {
          const isScrollbarClick =
            !!event.target.closest(".MuiDataGrid-virtualScroller") &&
            !event.target.closest(".MuiDataGrid-row");
          if (isScrollbarClick) return;

          const isEmpty =
            !currentValues.harvest_date &&
            !currentValues.cultivar_id &&
            !currentValues.room_id &&
            !currentValues.source_package_tag;
          if (isEmpty) {
            handleCancelRow();
          } else {
            handleSaveRow(currentValues);
          }
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleMenuOpen = (event, id) => {
    setAnchorEl(event.currentTarget);
    setMenuRowId(id);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuRowId(null);
  };

  const handleAddRow = () => {
    const newRow = {
      id: `new_${Date.now()}`,
      week_of_harvest: "",
      harvest_date: null,
      cultivar_id: "",
      cultivar_name: "",
      room_id: "",
      room_name: "",
      plants: "",
      source_package_tag: "1A405030001A4510000",
      child_package_tag: "1A405030001A4510000",
      bulk_smalls_trim: "",
      weight: "",
      location: "Vault",
      isNew: true,
    };
    setPackages([newRow, ...packages]);
    setEditingId(newRow.id);
    setEditValues(newRow);
  };

  const handleEditRow = async (row) => {
    setEditingId(row.id);
    setEditValues({
      ...row,
      source_package_tag: row.source_package_tag_name || "1A405030001A4510000",
      child_package_tag: row.child_package_tag_name || "1A405030001A4510000",
    });
    handleMenuClose();

    if (row.harvest_date) {
      try {
        const response = await httpService.get(
          `harvest/room-cultivar?harvestedDate=${dayjs(row.harvest_date).format("YYYY-MM-DD")}`,
        );
        if (response?.data) {
          setRooms(response.data.rooms || []);
          setCultivars(response.data.cultivars || []);
        } else {
          setRooms([]);
          setCultivars([]);
        }
      } catch (error) {
        console.error("Error fetching room/cultivar data:", error);
      }
    } else {
      setRooms([]);
      setCultivars([]);
    }
  };

  const handleCancelRow = () => {
    setPackages((prev) => prev.filter((p) => !String(p.id).startsWith("new_")));
    setEditingId(null);
    setEditValues({});
  };

  const handleSaveRow = async (valuesToSave) => {
    const currentValues = valuesToSave || editValues;
    const currentId = editingIdRef.current || editingId;
    if (!currentValues || Object.keys(currentValues).length === 0) return;

    const resolvedSourceTag = resolveTagValue(currentValues.source_package_tag);
    const resolvedChildTag = resolveTagValue(currentValues.child_package_tag);
    const isEmpty =
      !currentValues.harvest_date &&
      !currentValues.cultivar_id &&
      !currentValues.room_id &&
      !resolvedSourceTag;
    if (isEmpty) {
      handleCancelRow();
      return;
    }

    setLoading(true);
    try {
      const isNew = String(currentId).startsWith("new_");
      const existingRow = packages.find((p) => p.id === currentId) || {};
      const payload = {
        week_of_harvest: parseInt(currentValues.week_of_harvest) || 0,
        harvest_date: currentValues.harvest_date
          ? dayjs(currentValues.harvest_date).format("YYYY-MM-DD")
          : null,
        cultivar_id: parseInt(currentValues.cultivar_id) || null,
        room_id: parseInt(currentValues.room_id) || null,
        plants: parseInt(currentValues.plants) || 0,
        totes: currentValues.totes || "",
        source_package_tag: resolvedSourceTag,
        child_package_tag: resolveTagValue(
          existingRow.child_package_tag_name ||
            existingRow.child_package_tag ||
            currentValues.child_package_tag,
        ),
        bulk_smalls_trim:
          existingRow.bulk_smalls_trim !== undefined
            ? existingRow.bulk_smalls_trim
            : currentValues.bulk_smalls_trim || "",
        weight:
          existingRow.weight !== undefined && existingRow.weight !== null
            ? existingRow.weight
            : currentValues.weight === "" ||
                currentValues.weight == null ||
                isNaN(parseFloat(currentValues.weight))
              ? null
              : parseFloat(currentValues.weight),
        location: existingRow.location || currentValues.location || "Vault",
        selected_matric_ids: currentValues.selected_matric_ids || [],
      };

      if (isNew) {
        await httpService.post("harvest-readytopackage", payload);
        toast.success("Package added successfully", { position: "top-right" });
      } else {
        await httpService.put(`harvest-readytopackage/${currentId}`, payload);
        toast.success("Package updated successfully", {
          position: "top-right",
        });
      }
      setEditingId(null);
      setEditValues({});
      fetchReadyToPackageData();
    } catch (error) {
      const errMsg =
        error?.response?.data?.message ||
        (isNew ? "Failed to save package" : "Failed to update package");
      toast.error(errMsg, { position: "top-right" });
    }
    setLoading(false);
  };

  const handleOpenAddChildModal = (row) => {
    setAddChildRow(row);
    setDeletedPackageIds([]);
    const prefix = "1A405030001A4510000";
    if (row.child_packages && row.child_packages.length > 0) {
      setAddChildEntries(
        row.child_packages.map((pkg) => ({
          id: pkg.id,
          bulk_smalls_trim: pkg.bulk_smalls_trim || "",
          child_package_tag:
            pkg.child_package_tag_name || pkg.child_package_tag || prefix,
          weight: pkg.weight != null ? pkg.weight : "",
          location: pkg.location || "",
        })),
      );
    } else if (row.child_package_tag_name || row.child_package_tag) {
      setAddChildEntries([
        {
          bulk_smalls_trim: row.bulk_smalls_trim || "",
          child_package_tag:
            row.child_package_tag_name || row.child_package_tag,
          weight: row.weight != null ? row.weight : "",
          location: row.location || "",
        },
      ]);
    } else {
      setAddChildEntries([
        {
          bulk_smalls_trim: "",
          child_package_tag: prefix,
          weight: "",
          location: "",
        },
      ]);
    }
    setAddChildModalOpen(true);
  };

  const handleAddChildEntry = () => {
    const prefix = "1A405030001A4510000";
    setAddChildEntries((prev) => [
      ...prev,
      {
        bulk_smalls_trim: "",
        child_package_tag: prefix,
        weight: "",
        location: "",
      },
    ]);
  };

  const handleRemoveChildEntry = (index) => {
    const entryToRemove = addChildEntries[index];
    if (entryToRemove?.id) {
      setDeletedPackageIds((prev) => [...prev, entryToRemove.id]);
    }
    setAddChildEntries((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpdateChildEntry = (index, field, value) => {
    setAddChildEntries((prev) => {
      const next = [...prev];
      if (field === "bulk_smalls_trim") {
        if (value === "Bulk") {
          next[index] = {
            ...next[index],
            [field]: value,
            child_package_tag: "",
          };
        } else if (!next[index].child_package_tag) {
          next[index] = {
            ...next[index],
            [field]: value,
            child_package_tag: "1A405030001A4510000",
          };
        } else {
          next[index] = { ...next[index], [field]: value };
        }
      } else {
        next[index] = { ...next[index], [field]: value };
      }
      return next;
    });
  };

  const handleChildTagChange = (index, rawVal) => {
    const prefix = "1A405030001A4510000";
    let val = rawVal;
    if (!val.startsWith(prefix)) {
      val = prefix;
    }
    let afterPrefix = val.substring(prefix.length).replace(/\D/g, "");
    if (afterPrefix.length > 5) {
      afterPrefix = afterPrefix.substring(0, 5);
    }
    val = prefix + afterPrefix;
    handleUpdateChildEntry(index, "child_package_tag", val);
  };

  const handleSaveAddChildPackage = async () => {
    if (!addChildRow || addChildEntries.length === 0) return;

    setLoading(true);
    try {
      const upsertPayload = [];
      for (const entry of addChildEntries) {
        const resolvedTag =
          entry.bulk_smalls_trim === "Bulk"
            ? null
            : resolveTagValue(entry.child_package_tag);

        const pkgPayload = {
          child_package_tag: resolvedTag,
          bulk_smalls_trim: entry.bulk_smalls_trim || null,
          weight:
            entry.weight === "" ||
            entry.weight == null ||
            isNaN(parseFloat(entry.weight))
              ? null
              : parseFloat(entry.weight),
          location: entry.location || null,
        };

        if (entry.id) {
          pkgPayload.id = entry.id;
        }

        if (resolvedTag || entry.bulk_smalls_trim || entry.weight) {
          upsertPayload.push(pkgPayload);
        }
      }

      await httpService.post("harvest-readytopackage/packages/bulk", {
        readytopackage_id: addChildRow.id,
        upsert: upsertPayload,
        delete: deletedPackageIds,
      });

      toast.success("Child package(s) saved successfully", {
        position: "top-right",
      });
      setAddChildModalOpen(false);
      setAddChildRow(null);
      setDeletedPackageIds([]);
      fetchReadyToPackageData();
    } catch (error) {
      const errMsg =
        error?.response?.data?.message || "Failed to save child package(s)";
      toast.error(errMsg, { position: "top-right" });
    }
    setLoading(false);
  };

  const handleFieldChange = async (field, value) => {
    let updatedValues = { ...editValues, [field]: value };

    if (field === "harvest_date" && value) {
      const getWeekOfYear = (date) => {
        const d = new Date(date);
        const yearStart = new Date(d.getFullYear(), 0, 1);
        return Math.ceil(
          ((d - yearStart) / 86400000 + yearStart.getDay() + 1) / 7,
        );
      };
      const weekNumber = getWeekOfYear(value);
      updatedValues = { ...updatedValues, week_of_harvest: weekNumber };

      try {
        const response = await httpService.get(
          `harvest/room-cultivar?harvestedDate=${value}`,
        );
        if (response?.data) {
          setRooms(response.data.rooms || []);
          setCultivars(response.data.cultivars || []);
          updatedValues = {
            ...updatedValues,
            room_id: "",
            cultivar_id: "",
            plants: "",
          };
        }
      } catch (error) {
        console.error("Error fetching room/cultivar data:", error);
        setRooms([]);
        setCultivars([]);
      }
    }

    if (
      (field === "cultivar_id" || field === "room_id") &&
      updatedValues.cultivar_id &&
      updatedValues.room_id &&
      updatedValues.harvest_date
    ) {
      try {
        const response = await httpService.get(
          `harvest/plant-count?plantId=${updatedValues.cultivar_id}&roomId=${updatedValues.room_id}&harvestedDate=${updatedValues.harvest_date}`,
        );
        const count =
          response?.data?.count ?? response?.data?.data ?? response?.data;
        if (count !== undefined && count !== null) {
          updatedValues = { ...updatedValues, plants: parseInt(count) || 0 };
        }
      } catch (error) {
        console.error("Error fetching plant count:", error);
      }
    }

    setEditValues(updatedValues);
  };

  const handleDeleteRow = async () => {
    if (!selectedActionId) return;
    setLoading(true);
    try {
      await httpService.delete(
        `harvest-readytopackage/delete/${selectedActionId}`,
      );
      toast.success("Package deleted successfully", { position: "top-right" });
      setDeleteModalOpen(false);
      setSelectedActionId(null);
      fetchReadyToPackageData();
    } catch (error) {
      toast.error("Failed to delete package", { position: "top-right" });
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      field: "action",
      headerName: "Action",
      width: 100,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <>
          {editingId === params.row.id ? (
            <IconButton onClick={handleCancelRow} color="error">
              <MdClose />
            </IconButton>
          ) : (
            <>
              <IconButton onClick={(e) => handleMenuOpen(e, params.row.id)}>
                <GridMoreVertIcon />
              </IconButton>
              {anchorEl && menuRowId === params.row.id && (
                <Popover
                  className="popup-menu"
                  open={Boolean(anchorEl)}
                  anchorEl={anchorEl}
                  onClose={handleMenuClose}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                  <MenuItem
                    onClick={() => handleEditRow(params.row)}
                    className="menu-list-items"
                    disabled={!!params.row.move_to}
                  >
                    <div className="menu-list-items-text">Edit</div>
                    <CiEdit style={{ cursor: "pointer" }} />
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleOpenAddChildModal(params.row);
                      handleMenuClose();
                    }}
                    className="menu-list-items"
                    disabled={!!params.row.move_to}
                  >
                    <div className="menu-list-items-text">Edit Child Tag</div>
                    <CiEdit style={{ cursor: "pointer" }} />
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setSelectedActionId(params.row.id);
                      setDeleteModalOpen(true);
                      handleMenuClose();
                    }}
                    className="menu-list-items"
                    disabled={!!params.row.move_to}
                  >
                    <div className="menu-list-items-text">Delete</div>
                    <GoTrash style={{ cursor: "pointer" }} />
                  </MenuItem>
                </Popover>
              )}
            </>
          )}
        </>
      ),
    },
    {
      field: "week_of_harvest",
      headerName: "Week of Harvest",
      flex: 1,
      minWidth: 150,
      renderCell: (params) =>
        editingId === params.row.id ? (
          <input
            type="number"
            value={editValues.week_of_harvest || ""}
            onChange={(e) =>
              handleFieldChange("week_of_harvest", e.target.value)
            }
            style={{
              width: "100%",
              padding: "8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          />
        ) : (
          params.value || "-"
        ),
    },
    {
      field: "harvest_date",
      headerName: "Harvest Date",
      flex: 1,
      minWidth: 200,
      renderCell: (params) =>
        editingId === params.row.id ? (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={
                editValues.harvest_date ? dayjs(editValues.harvest_date) : null
              }
              onChange={(newValue) => {
                const dateValue = newValue ? newValue.format("YYYY-MM-DD") : "";
                handleFieldChange("harvest_date", dateValue);
              }}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                },
              }}
              disableFuture
            />
          </LocalizationProvider>
        ) : params.value ? (
          dayjs(params.value).format("MM-DD-YYYY")
        ) : (
          "-"
        ),
    },
    {
      field: "cultivar_name",
      headerName: "Cultivar",
      flex: 1,
      minWidth: 180,
      renderCell: (params) =>
        editingId === params.row.id ? (
          <FormControl fullWidth size="small">
            <Select
              value={editValues.cultivar_id || ""}
              onChange={(e) => handleFieldChange("cultivar_id", e.target.value)}
              native
            >
              <option value="">Select Cultivar</option>
              {cultivars.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </FormControl>
        ) : (
          params.value || "-"
        ),
    },
    {
      field: "room_name",
      headerName: "Room",
      flex: 1,
      minWidth: 180,
      renderCell: (params) =>
        editingId === params.row.id ? (
          <FormControl fullWidth size="small">
            <Select
              value={editValues.room_id || ""}
              onChange={(e) => handleFieldChange("room_id", e.target.value)}
              native
            >
              <option value="">Select Room</option>
              {rooms.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </Select>
          </FormControl>
        ) : (
          params.value || "-"
        ),
    },
    {
      field: "plants",
      headerName: "Plants",
      flex: 1,
      minWidth: 210,
      renderCell: (params) =>
        editingId === params.row.id ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              width: "100%",
              overflow: "hidden",
            }}
          >
            <input
              type="number"
              value={editValues.plants !== undefined ? editValues.plants : ""}
              readOnly
              style={{
                width: "50px",
                minWidth: "45px",
                flexShrink: 0,
                padding: "5px 6px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                boxSizing: "border-box",
                backgroundColor: "#f0f0f0",
                cursor: "not-allowed",
              }}
            />
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setSelectMatricModalOpen(true);
              }}
              style={{
                textTransform: "none",
                fontSize: "11px",
                padding: "4px 8px",
                whiteSpace: "nowrap",
                borderColor: "#00A272",
                color: "#00A272",
                flexShrink: 0,
              }}
            >
              Select Plant Tag
            </Button>
          </div>
        ) : (
          <span
            style={{
              textDecoration: "underline",
              color: "#427fcfff",
              cursor: "pointer",
            }}
            onClick={() => handleOpenMatricModal(params.row)}
          >
            {params.value || 0}
          </span>
        ),
    },
    {
      field: "totes",
      headerName: "Totes",
      flex: 1,
      minWidth: 150,
      renderCell: (params) =>
        editingId === params.row.id ? (
          <input
            type="text"
            value={editValues.totes || ""}
            onChange={(e) => handleFieldChange("totes", e.target.value)}
            style={{
              width: "100%",
              padding: "8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          />
        ) : (
          params.value || "-"
        ),
    },
    {
      field: "source_package_tag",
      headerName: "Source Package Tag",
      flex: 1,
      minWidth: 350,
      renderCell: (params) => {
        const prefix = "1A405030001A4510000";
        const val = editValues.source_package_tag || "";
        let afterPrefix = "";
        if (val.startsWith(prefix)) {
          afterPrefix = val.substring(prefix.length).replace(/\D/g, "");
        } else {
          afterPrefix = val.replace(/\D/g, "").substring(0, 5);
        }
        return editingId === params.row.id ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              gap: "4px",
              padding: "4px 0",
            }}
          >
            <input
              type="text"
              placeholder="Last 5 digits or full tag"
              value={editValues.source_package_tag || ""}
              onChange={(e) => {
                let val = e.target.value;
                const prefix = "1A405030001A4510000";
                if (!val.startsWith(prefix)) {
                  val = prefix;
                }
                let afterPrefix = val
                  .substring(prefix.length)
                  .replace(/\D/g, "");
                if (afterPrefix.length > 5) {
                  afterPrefix = afterPrefix.substring(0, 5);
                }
                val = prefix + afterPrefix;
                setEditValues((prev) => ({
                  ...prev,
                  source_package_tag: val,
                  source_package_tag_name: val,
                }));
              }}
              style={{
                width: "100%",
                padding: "6px 8px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                boxSizing: "border-box",
              }}
            />
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "11px",
                color: "#666",
              }}
            >
              <span style={{ fontFamily: "monospace", opacity: 0.8 }}>
                Prefix: {prefix}
              </span>
              <div style={{ display: "flex", gap: "4px" }}>
                {[0, 1, 2, 3, 4].map((index) => {
                  const char = afterPrefix[index];
                  return (
                    <span
                      key={index}
                      style={{
                        display: "inline-block",
                        width: "12px",
                        textAlign: "center",
                        borderBottom: char
                          ? "2px solid #00A272"
                          : "2px solid #c4c4c4",
                        color: char ? "#00A272" : "#a1a2a1",
                        fontFamily: "monospace",
                        fontWeight: "bold",
                        fontSize: "12px",
                      }}
                    >
                      {char || "_"}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          params.row.source_package_tag_name || params.value || "-"
        );
      },
    },
    {
      field: "child_package_tag",
      headerName: "Child Package Tag",
      flex: 1,
      minWidth: 350,
      renderCell: (params) => {
        /*
        // Inline edit code for child package tag (commented out for now):
        const prefix = "1A405030001A4510000";
        const val = editValues.child_package_tag || "";
        let afterPrefix = "";
        if (val.startsWith(prefix)) {
          afterPrefix = val.substring(prefix.length).replace(/\D/g, "");
        } else {
          afterPrefix = val.replace(/\D/g, "").substring(0, 5);
        }
        if (editingId === params.row.id) {
          return (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                gap: "4px",
                padding: "4px 0",
              }}
            >
              <input
                type="text"
                disabled={editValues.bulk_smalls_trim === "Bulk"}
                placeholder={
                  editValues.bulk_smalls_trim === "Bulk"
                    ? "Disabled for Bulk"
                    : "Last 5 digits or full tag"
                }
                value={
                  editValues.bulk_smalls_trim === "Bulk"
                    ? ""
                    : editValues.child_package_tag || ""
                }
                onChange={(e) => {
                  let val = e.target.value;
                  const prefix = "1A405030001A4510000";
                  if (!val.startsWith(prefix)) {
                    val = prefix;
                  }
                  let afterPrefix = val
                    .substring(prefix.length)
                    .replace(/\D/g, "");
                  if (afterPrefix.length > 5) {
                    afterPrefix = afterPrefix.substring(0, 5);
                  }
                  val = prefix + afterPrefix;
                  setEditValues((prev) => ({
                    ...prev,
                    child_package_tag: val,
                    child_package_tag_name: val,
                  }));
                }}
                style={{
                  width: "100%",
                  padding: "6px 8px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  boxSizing: "border-box",
                  backgroundColor:
                    editValues.bulk_smalls_trim === "Bulk" ? "#f5f5f5" : "#fff",
                  cursor:
                    editValues.bulk_smalls_trim === "Bulk"
                      ? "not-allowed"
                      : "text",
                }}
              />
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: "11px",
                  color: "#666",
                }}
              >
                <span style={{ fontFamily: "monospace", opacity: 0.8 }}>
                  Prefix: {prefix}
                </span>
                <div style={{ display: "flex", gap: "4px" }}>
                  {[0, 1, 2, 3, 4].map((index) => {
                    const char = afterPrefix[index];
                    return (
                      <span
                        key={index}
                        style={{
                          display: "inline-block",
                          width: "12px",
                          textAlign: "center",
                          borderBottom: char
                            ? "2px solid #00A272"
                            : "2px solid #c4c4c4",
                          color: char ? "#00A272" : "#a1a2a1",
                          fontFamily: "monospace",
                          fontWeight: "bold",
                          fontSize: "12px",
                        }}
                      >
                        {char || "_"}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        }
        */

        const hasChild =
          (params.row.child_packages && params.row.child_packages.length > 0) ||
          Boolean(params.row.child_package_tag_name) ||
          Boolean(params.row.child_package_tag);

        if (hasChild) {
          return (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                width: "calc(100% + 20px)",
                margin: "0 -10px",
                height: "100%",
                justifyContent: "center",
              }}
            >
              {params.row.child_packages &&
              params.row.child_packages.length > 0 ? (
                params.row.child_packages.map((pkg, idx) => (
                  <div
                    key={pkg.id || idx}
                    style={{
                      minHeight: "36px",
                      display: "flex",
                      alignItems: "center",
                      fontSize: "13px",
                      padding: "0 10px",
                      borderBottom:
                        idx < params.row.child_packages.length - 1
                          ? "1px solid #E0E0E0"
                          : "none",
                      boxSizing: "border-box",
                    }}
                  >
                    {pkg.child_package_tag_name || "-"}
                  </div>
                ))
              ) : (
                <div
                  style={{
                    minHeight: "28px",
                    display: "flex",
                    alignItems: "center",
                    fontSize: "13px",
                    padding: "0 10px",
                  }}
                >
                  {params.row.child_package_tag_name ||
                    params.row.child_package_tag ||
                    params.value ||
                    "-"}
                </div>
              )}
            </div>
          );
        }

        return (
          <div
            onClick={(e) => {
              e.stopPropagation();
              handleOpenAddChildModal(params.row);
            }}
            style={{
              color: "#00A272",
              cursor: "pointer",
              fontWeight: "bold",
              display: "inline-flex",
              alignItems: "center",
              gap: "3px",
              whiteSpace: "nowrap",
              fontSize: "12px",
              padding: "2px 6px",
              borderRadius: "4px",
              backgroundColor: "#E6F6F1",
            }}
            title="Add child package"
          >
            <RxPlus /> Add Child Tag
          </div>
        );
      },
    },
    {
      field: "bulk_smalls_trim",
      headerName: "Bulk/Smalls/Trim",
      flex: 1,
      minWidth: 180,
      renderCell: (params) => {
        /*
        // Inline edit code for bulk_smalls_trim (commented out for now):
        if (editingId === params.row.id) {
          return (
            <FormControl fullWidth size="small">
              <Select
                value={editValues.bulk_smalls_trim || ""}
                onChange={(e) =>
                  handleFieldChange("bulk_smalls_trim", e.target.value)
                }
                native
              >
                <option value="">Select Type</option>
                <option value="Bulk">Bulk</option>
                <option value="Smalls">Smalls</option>
                <option value="Trim">Trim</option>
              </Select>
            </FormControl>
          );
        }
        */
        return params.row.child_packages &&
          params.row.child_packages.length > 0 ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              width: "calc(100% + 20px)",
              margin: "0 -10px",
              height: "100%",
              justifyContent: "center",
            }}
          >
            {params.row.child_packages.map((pkg, idx) => (
              <div
                key={pkg.id || idx}
                style={{
                  minHeight: "36px",
                  display: "flex",
                  alignItems: "center",
                  fontSize: "13px",
                  padding: "0 10px",
                  borderBottom:
                    idx < params.row.child_packages.length - 1
                      ? "1px solid #E0E0E0"
                      : "none",
                  boxSizing: "border-box",
                }}
              >
                {pkg.bulk_smalls_trim || "-"}
              </div>
            ))}
          </div>
        ) : (
          params.value || "-"
        );
      },
    },
    {
      field: "weight",
      headerName: "Weight",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        /*
        // Inline edit code for weight (commented out for now):
        if (editingId === params.row.id) {
          return (
            <input
              type="number"
              step="any"
              value={editValues.weight || ""}
              onChange={(e) => handleFieldChange("weight", e.target.value)}
              style={{
                width: "100%",
                padding: "8px",
                border: "1px solid #ccc",
                borderRadius: "4px",
              }}
            />
          );
        }
        */
        return params.row.child_packages &&
          params.row.child_packages.length > 0 ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              width: "calc(100% + 20px)",
              margin: "0 -10px",
              height: "100%",
              justifyContent: "center",
            }}
          >
            {params.row.child_packages.map((pkg, idx) => (
              <div
                key={pkg.id || idx}
                style={{
                  minHeight: "36px",
                  display: "flex",
                  alignItems: "center",
                  fontSize: "13px",
                  padding: "0 10px",
                  borderBottom:
                    idx < params.row.child_packages.length - 1
                      ? "1px solid #E0E0E0"
                      : "none",
                  boxSizing: "border-box",
                }}
              >
                {pkg.weight != null && pkg.weight !== ""
                  ? `${pkg.weight} lbs`
                  : "-"}
              </div>
            ))}
          </div>
        ) : params.value ? (
          `${params.value} lbs`
        ) : (
          "-"
        );
      },
    },
    {
      field: "location",
      headerName: "Location",
      flex: 1,
      minWidth: 180,
      renderCell: (params) => {
        /*
        // Inline edit code for location (commented out for now):
        if (editingId === params.row.id) {
          return (
            <FormControl fullWidth size="small">
              <Select
                value={editValues.location || ""}
                onChange={(e) => handleFieldChange("location", e.target.value)}
                native
              >
                <option value="">Select Location</option>
                <option value="Trim Room">Trim Room</option>
                <option value="Vault">Vault</option>
              </Select>
            </FormControl>
          );
        }
        */
        return params.row.child_packages &&
          params.row.child_packages.length > 0 ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              width: "calc(100% + 20px)",
              margin: "0 -10px",
              height: "100%",
              justifyContent: "center",
            }}
          >
            {params.row.child_packages.map((pkg, idx) => (
              <div
                key={pkg.id || idx}
                style={{
                  minHeight: "36px",
                  display: "flex",
                  alignItems: "center",
                  fontSize: "13px",
                  padding: "0 10px",
                  borderBottom:
                    idx < params.row.child_packages.length - 1
                      ? "1px solid #E0E0E0"
                      : "none",
                  boxSizing: "border-box",
                }}
              >
                {pkg.location || "-"}
              </div>
            ))}
          </div>
        ) : (
          params.value || "-"
        );
      },
    },
  ];

  const getSourcePackageTagId = () => {
    const tagVal = editValues.source_package_tag;
    if (tagVal) {
      if (typeof tagVal === "number") return tagVal;
      if (!isNaN(Number(tagVal)) && String(tagVal).length < 10) return Number(tagVal);
      const resolvedStr = resolveTagValue(tagVal);
      const found = (importTags || []).find(
        (t) =>
          String(t.tag) === String(tagVal) ||
          String(t.tag) === String(resolvedStr) ||
          String(t.id) === String(tagVal),
      );
      if (found) return found.id;
    }
    const row = packages.find((p) => p.id === editingId);
    return row && typeof row.source_package_tag === "number"
      ? row.source_package_tag
      : null;
  };

  const totalRowHeight = packages.reduce((acc, row) => {
    const childPkgs = row.child_packages;
    const hasMultipleChildPkgs = childPkgs && childPkgs.length > 1;
    const childHeight = hasMultipleChildPkgs
      ? childPkgs.length * 36 + 16
      : 52;
    const rowHeight = row.id === editingId ? Math.max(75, childHeight) : childHeight;
    return acc + (rowHeight || 52);
  }, 0);

  const isAutoHeight = totalRowHeight + 56 <= 576;

  return (
    <>
      {loading && <FullScreenLoader />}
      <div className="harvest">
        <div className="page-title">
          <div className="title-wrap">
            <div
              className="table-filter-wrap"
              style={{ paddingBottom: 0, justifyContent: "flex-start" }}
            >
              <div className="header_search_bar">
                <div className="input-group input_group">
                  <button className="btn search-btn search_btn">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="15"
                      height="15"
                      viewBox="0 0 21 21"
                      fill="none"
                    >
                      <circle
                        cx="9.98856"
                        cy="9.98856"
                        r="8.98856"
                        stroke="#495057"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M16.2402 16.7071L19.7643 20.222"
                        stroke="#495057"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <input
                    type="search"
                    className="form-control form_control"
                    placeholder="Search..."
                    aria-label="Search"
                    onChange={(e) => setSearchData(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="frozen-table-wrapper" style={{ width: "100%" }}>
          {/* Fake frozen header */}
          <div className="fake-frozen-header">
            <div
              className="fake-header-cell"
              style={{
                width: 100,
                backgroundColor: "#e7e7ff",
                fontFamily: "inherit",
                justifyContent: "center",
              }}
            >
              Action
            </div>
          </div>
          <DataGrid
            rows={packages}
            columns={columns}
            autoHeight={isAutoHeight}
            getRowHeight={(params) => {
              const rowData =
                params.model || packages.find((p) => p.id === params.id);
              const childPkgs = rowData?.child_packages;
              const hasMultipleChildPkgs = childPkgs && childPkgs.length > 1;
              const childHeight = hasMultipleChildPkgs
                ? childPkgs.length * 36 + 16
                : null;
              if (params.id === editingId) {
                return Math.max(75, childHeight || 75);
              }
              return childHeight;
            }}
            disableRowSelectionOnClick
            columnBuffer={columns.length}
            sx={{
              border: "1px solid #E0E0E0",
              height: isAutoHeight ? "auto" : 576,
              maxHeight: 576,
              [`& .${gridClasses.cell}`]: { py: 1 },
              "& .MuiDataGrid-cell[data-field='action']": {
                position: "sticky !important",
                left: "0 !important",
                zIndex: "2 !important",
                backgroundColor: "#fff !important",
                borderRight: "1px solid #E0E0E0 !important",
                boxShadow: "2px 0 5px -2px rgba(0, 0, 0, 0.2) !important",
              },
              "& .MuiDataGrid-row:hover .MuiDataGrid-cell[data-field='action']":
                {
                  backgroundColor: "#F5F5F7 !important",
                },
              "& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell[data-field='action']":
                {
                  backgroundColor: "#E6F6F1 !important",
                },
            }}
            hideFooter
            showCellVerticalBorder
            showColumnVerticalBorder
          />
        </div>

        {/* Delete Modal */}
        <Modal open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
          <div className="modal_block">
            <div className="modal_header">
              <div className="modal_title_wrapper">
                <h2 className="modal_title">Are you Sure?</h2>
              </div>
              <div className="cancel-btn-wrap">
                <RxCross1 onClick={() => setDeleteModalOpen(false)} />
              </div>
            </div>
            <div className="modal_content">
              <div className="modal_content_inner">
                <div>
                  <TbAlertHexagonFilled />
                </div>
                <div>
                  <p>Do you want to delete?</p>
                </div>
              </div>
            </div>
            <div className="modal_footer">
              <button
                onClick={() => setDeleteModalOpen(false)}
                className="btn btn-border"
              >
                No
              </button>
              <button onClick={handleDeleteRow} className="btn btn-primary">
                Yes
              </button>
            </div>
          </div>
        </Modal>

        {/* Add Child Package Modal */}
        <Modal
          open={addChildModalOpen}
          onClose={() => setAddChildModalOpen(false)}
        >
          <div
            className="modal_block"
            style={{ width: "95%", maxWidth: "980px" }}
          >
            <div className="modal_header">
              <div
                className="modal_title_wrapper"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  flexWrap: "wrap",
                }}
              >
                <h2 className="modal_title" style={{ margin: 0 }}>
                  Add Child Package
                </h2>
                {addChildRow &&
                  (addChildRow.source_package_tag_name ||
                    addChildRow.source_package_tag) && (
                    <span
                      style={{
                        fontSize: "13px",
                        fontWeight: "normal",
                        color: "#495057",
                        backgroundColor: "#F1F3F5",
                        padding: "3px 10px",
                        borderRadius: "16px",
                        border: "1px solid #DEE2E6",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <span style={{ fontWeight: 600, color: "#212529" }}>
                        Source Package Tag :
                      </span>
                      <span
                        style={{
                          fontFamily: "monospace",
                          color: "#00A272",
                          fontWeight: 600,
                        }}
                      >
                        {addChildRow.source_package_tag_name ||
                          addChildRow.source_package_tag}
                      </span>
                    </span>
                  )}
              </div>
              <div className="cancel-btn-wrap">
                <RxCross1 onClick={() => setAddChildModalOpen(false)} />
              </div>
            </div>

            <div
              className="modal_content"
              style={{ maxHeight: "65vh", overflowY: "auto" }}
            >
              {/* Header Row */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "180px minmax(320px, 1fr) 120px 180px 90px",
                  gap: "14px",
                  alignItems: "center",
                  paddingBottom: "8px",
                  borderBottom: "1px solid #eee",
                  marginBottom: "14px",
                  fontWeight: 600,
                  fontSize: "13px",
                  color: "#444",
                }}
              >
                <div>Bulk/Smalls/Trim</div>
                <div>Child Package Tag</div>
                <div>Weight</div>
                <div>Location</div>
                {/* <div style={{ textAlign: "center" }}>Action</div> */}
              </div>

              {/* Multiple Child Package Rows */}
              {addChildEntries.map((entry, index) => {
                const prefix = "1A405030001A4510000";
                const val = entry.child_package_tag || "";
                let afterPrefix = "";
                if (val.startsWith(prefix)) {
                  afterPrefix = val.substring(prefix.length).replace(/\D/g, "");
                } else {
                  afterPrefix = val.replace(/\D/g, "").substring(0, 5);
                }

                return (
                  <div
                    key={index}
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "180px minmax(320px, 1fr) 120px 180px 90px",
                      gap: "14px",
                      alignItems: "flex-start",
                      marginBottom: "16px",
                    }}
                  >
                    {/* Bulk/Smalls/Trim */}
                    <div>
                      <FormControl fullWidth size="small">
                        <Select
                          value={entry.bulk_smalls_trim}
                          onChange={(e) =>
                            handleUpdateChildEntry(
                              index,
                              "bulk_smalls_trim",
                              e.target.value,
                            )
                          }
                          native
                          sx={{
                            backgroundColor: "#fff",
                            fontSize: "14px",
                            height: "40px",
                            "& select": {
                              fontSize: "14px",
                              padding: "8.5px 14px",
                            },
                          }}
                        >
                          <option value="">Select Type</option>
                          <option value="Bulk">Bulk</option>
                          <option value="Smalls">Smalls</option>
                          <option value="Trim">Trim</option>
                        </Select>
                      </FormControl>
                    </div>

                    {/* Child Package Tag */}
                    <div>
                      <input
                        type="text"
                        disabled={entry.bulk_smalls_trim === "Bulk"}
                        placeholder={
                          entry.bulk_smalls_trim === "Bulk"
                            ? "Disabled for Bulk"
                            : "Last 5 digits or full tag"
                        }
                        value={
                          entry.bulk_smalls_trim === "Bulk"
                            ? ""
                            : entry.child_package_tag || ""
                        }
                        onChange={(e) =>
                          handleChildTagChange(index, e.target.value)
                        }
                        style={{
                          width: "100%",
                          padding: "8.5px 12px",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                          boxSizing: "border-box",
                          fontSize: "14px",
                          height: "40px",
                          outline: "none",
                          backgroundColor:
                            entry.bulk_smalls_trim === "Bulk"
                              ? "#f5f5f5"
                              : "#fff",
                          cursor:
                            entry.bulk_smalls_trim === "Bulk"
                              ? "not-allowed"
                              : "text",
                          color:
                            entry.bulk_smalls_trim === "Bulk" ? "#999" : "#333",
                        }}
                      />
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          fontSize: "11px",
                          color: "#666",
                          marginTop: "4px",
                          padding: "0 2px",
                          opacity: entry.bulk_smalls_trim === "Bulk" ? 0.35 : 1,
                        }}
                      >
                        <span
                          style={{ fontFamily: "monospace", opacity: 0.85 }}
                        >
                          Prefix: {prefix}
                        </span>
                        <div style={{ display: "flex", gap: "3px" }}>
                          {[0, 1, 2, 3, 4].map((digitIdx) => {
                            const char =
                              entry.bulk_smalls_trim === "Bulk"
                                ? ""
                                : afterPrefix[digitIdx];
                            return (
                              <span
                                key={digitIdx}
                                style={{
                                  display: "inline-block",
                                  width: "13px",
                                  textAlign: "center",
                                  borderBottom: char
                                    ? "2px solid #00A272"
                                    : "2px solid #c4c4c4",
                                  color: char ? "#00A272" : "#a1a2a1",
                                  fontFamily: "monospace",
                                  fontWeight: "bold",
                                  fontSize: "12px",
                                }}
                              >
                                {char || "_"}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Weight */}
                    <div>
                      <input
                        type="number"
                        step="any"
                        placeholder="Weight"
                        value={entry.weight}
                        onChange={(e) =>
                          handleUpdateChildEntry(
                            index,
                            "weight",
                            e.target.value,
                          )
                        }
                        style={{
                          width: "100%",
                          padding: "8.5px 12px",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                          boxSizing: "border-box",
                          fontSize: "14px",
                          height: "40px",
                          outline: "none",
                        }}
                      />
                    </div>

                    {/* Location */}
                    <div>
                      <FormControl fullWidth size="small">
                        <Select
                          value={entry.location}
                          onChange={(e) =>
                            handleUpdateChildEntry(
                              index,
                              "location",
                              e.target.value,
                            )
                          }
                          native
                          sx={{
                            backgroundColor: "#fff",
                            fontSize: "14px",
                            height: "40px",
                            "& select": {
                              fontSize: "14px",
                              padding: "8.5px 14px",
                            },
                          }}
                        >
                          <option value="">Select Location</option>
                          <option value="Trim Room">Trim Room</option>
                          <option value="Vault">Vault</option>
                        </Select>
                      </FormControl>
                    </div>

                    {/* Actions (+ / delete buttons) */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "6px",
                        height: "40px",
                      }}
                    >
                      {index === addChildEntries.length - 1 && (
                        <button
                          type="button"
                          onClick={handleAddChildEntry}
                          style={{
                            backgroundColor: "#00A272",
                            color: "#fff",
                            border: "none",
                            borderRadius: "4px",
                            width: "36px",
                            height: "36px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            fontSize: "18px",
                          }}
                          title="Add another child package"
                        >
                          <RxPlus />
                        </button>
                      )}
                      {addChildEntries.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveChildEntry(index)}
                          style={{
                            backgroundColor: "#ff4d4f",
                            color: "#fff",
                            border: "none",
                            borderRadius: "4px",
                            width: "36px",
                            height: "36px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            fontSize: "15px",
                          }}
                          title="Delete this package"
                        >
                          <GoTrash />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="modal_footer">
              <button
                type="button"
                onClick={() => setAddChildModalOpen(false)}
                className="btn btn-border"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveAddChildPackage}
                className="btn btn-primary"
              >
                Save
              </button>
            </div>
          </div>
        </Modal>
        <MatricIdsListModal
          open={matricModalOpen}
          handleClose={() => {
            setMatricModalOpen(false);
            setSelectedMatricRow(null);
          }}
          row={selectedMatricRow}
        />

        {/* Select Plant Matric IDs Modal */}
        <SelectMatricModal
          open={selectMatricModalOpen}
          handleClose={() => setSelectMatricModalOpen(false)}
          cultivarId={editValues.cultivar_id}
          roomId={editValues.room_id}
          harvestDate={editValues.harvest_date}
          initialSelected={editValues.selected_matric_ids}
          // excludeBuckingId={
          //   editingId &&
          //   !String(editingId).startsWith("new_") 
          //     ? editingId
          //     : null
          // }
          excludeBuckingId={getSourcePackageTagId()}
          onApply={(selectedArray, selectedCount) => {
            setEditValues((prev) => ({
              ...prev,
              plants: selectedCount,
              selected_matric_ids: selectedArray,
            }));
          }}
        />
      </div>
    </>
  );
};

export default ReadyToPackage;
